/**
 * A rule's `actions` is already a tree, not just a flat list — a `branch`
 * action's action_parameters hold nested per-case action lists (see
 * backend/app/services/automation_actions.py's `branch` handler +
 * automation_engine.execute_action_list). This module adds a stable client-
 * side `_id` to every node (top-level or nested) so the flow editor can
 * address/mutate any node by id instead of a fragile positional path, and
 * provides the small set of tree operations the editor needs. No new data
 * shape is introduced — `_id` is stripped again before saving, so the wire
 * format is exactly what the API already accepts.
 */

export interface ActionCase {
  operator?: string;
  expected_value?: any;
  case_sensitive?: boolean;
  actions: ActionNode[];
  _id: string;
}

export interface ActionNode {
  _id: string;
  action_type: string;
  action_parameters?: Record<string, any>;
  execution_order?: number;
  continue_on_failure?: boolean;
}

let _seq = 0;
const newId = () => `n${Date.now().toString(36)}${(_seq++).toString(36)}`;

/** Tags every node (and nested branch case actions) with a stable _id. Safe
 * to call on already-tagged trees — existing _id fields are preserved. */
export function tagTree(actions: any[]): ActionNode[] {
  return (actions || []).map((a) => tagNode(a));
}

function tagNode(a: any): ActionNode {
  const node: ActionNode = { ...a, _id: a._id || newId() };
  if (node.action_type === 'branch' && node.action_parameters) {
    const params = { ...node.action_parameters };
    if (Array.isArray(params.cases)) {
      params.cases = params.cases.map((c: any) => ({
        ...c,
        _id: c._id || newId(),
        actions: tagTree(c.actions || []),
      }));
    }
    if (Array.isArray(params.default_actions)) {
      params.default_actions = tagTree(params.default_actions);
    }
    node.action_parameters = params;
  }
  return node;
}

/** Strips every _id (recursively) — the payload the save API expects. */
export function stripIds(actions: ActionNode[]): any[] {
  return (actions || []).map((a) => {
    const { _id, ...rest } = a as any;
    if (rest.action_type === 'branch' && rest.action_parameters) {
      const params = { ...rest.action_parameters };
      if (Array.isArray(params.cases)) {
        params.cases = params.cases.map((c: any) => {
          const { _id: caseId, ...caseRest } = c;
          return { ...caseRest, actions: stripIds(c.actions || []) };
        });
      }
      if (Array.isArray(params.default_actions)) {
        params.default_actions = stripIds(params.default_actions);
      }
      rest.action_parameters = params;
    }
    return rest;
  });
}

export function treeHasBranch(actions: ActionNode[]): boolean {
  return (actions || []).some((a) => {
    if (a.action_type === 'branch') return true;
    return false;
  });
}

/** Every list a node could live in, alongside a setter to write a new list
 * back into its parent. Used so find/update/remove/insert share one walk. */
type ListRef = { list: ActionNode[]; set: (next: ActionNode[]) => ActionNode[] };

function walkLists(root: ActionNode[], visit: (ref: ListRef) => ActionNode[] | void): ActionNode[] {
  const setRoot = (next: ActionNode[]) => next;
  const result = visit({ list: root, set: setRoot });
  if (result) return result;

  return root.map((node) => {
    if (node.action_type !== 'branch' || !node.action_parameters) return node;
    const params = { ...node.action_parameters };
    let changed = false;

    if (Array.isArray(params.cases)) {
      const nextCases = params.cases.map((c: any) => {
        const nextActions = walkLists(c.actions || [], visit);
        if (nextActions !== c.actions) changed = true;
        return { ...c, actions: nextActions };
      });
      params.cases = nextCases;
    }
    if (Array.isArray(params.default_actions)) {
      const nextDefault = walkLists(params.default_actions, visit);
      if (nextDefault !== params.default_actions) changed = true;
      params.default_actions = nextDefault;
    }
    if (!changed) return node;
    return { ...node, action_parameters: params };
  });
}

/** Replaces the node with the given id, wherever it lives in the tree.
 * `patch` may be a function of the current node so callers can merge into
 * nested fields (e.g. action_parameters) without a separate read step. */
export function updateNode(
  root: ActionNode[],
  id: string,
  patch: Partial<ActionNode> | ((n: ActionNode) => Partial<ActionNode>),
): ActionNode[] {
  return walkLists(root, ({ list }) => {
    if (!list.some((n) => n._id === id)) return undefined;
    return list.map((n) => (n._id === id ? { ...n, ...(typeof patch === 'function' ? patch(n) : patch) } : n));
  });
}

export function removeNode(root: ActionNode[], id: string): ActionNode[] {
  return walkLists(root, ({ list }) => {
    if (!list.some((n) => n._id === id)) return undefined;
    return list.filter((n) => n._id !== id);
  });
}

/** Inserts a fresh plain action node right after `afterId` in whichever list
 * contains it. Pass afterId=null to append to the given targetList directly
 * (used for "add first action" on an empty case). */
export function insertAfter(root: ActionNode[], afterId: string, node: ActionNode): ActionNode[] {
  return walkLists(root, ({ list }) => {
    const idx = list.findIndex((n) => n._id === afterId);
    if (idx === -1) return undefined;
    const next = [...list];
    next.splice(idx + 1, 0, node);
    return next;
  });
}

/** Appends a plain action node to a specific branch case's action list
 * (or that branch's default_actions, when caseId === 'default'). */
export function appendToCase(root: ActionNode[], branchId: string, caseId: string | 'default', node: ActionNode): ActionNode[] {
  return root.map((n) => {
    if (n._id === branchId && n.action_type === 'branch' && n.action_parameters) {
      const params = { ...n.action_parameters };
      if (caseId === 'default') {
        params.default_actions = [...(params.default_actions || []), node];
      } else {
        params.cases = (params.cases || []).map((c: any) =>
          c._id === caseId ? { ...c, actions: [...(c.actions || []), node] } : c
        );
      }
      return { ...n, action_parameters: params };
    }
    if (n.action_type === 'branch' && n.action_parameters) {
      const params = { ...n.action_parameters };
      let changed = false;
      if (Array.isArray(params.cases)) {
        params.cases = params.cases.map((c: any) => {
          const nextActions = appendToCase(c.actions || [], branchId, caseId, node);
          if (nextActions !== c.actions) changed = true;
          return { ...c, actions: nextActions };
        });
      }
      if (Array.isArray(params.default_actions)) {
        const nextDefault = appendToCase(params.default_actions, branchId, caseId, node);
        if (nextDefault !== params.default_actions) changed = true;
        params.default_actions = nextDefault;
      }
      return changed ? { ...n, action_parameters: params } : n;
    }
    return n;
  });
}

export function addCase(root: ActionNode[], branchId: string): ActionNode[] {
  return updateBranchParams(root, branchId, (params) => ({
    ...params,
    cases: [...(params.cases || []), { operator: 'equal_to', expected_value: '', actions: [], _id: newId() }],
  }));
}

export function removeCase(root: ActionNode[], branchId: string, caseId: string): ActionNode[] {
  return updateBranchParams(root, branchId, (params) => ({
    ...params,
    cases: (params.cases || []).filter((c: any) => c._id !== caseId),
  }));
}

export function updateCase(root: ActionNode[], branchId: string, caseId: string, patch: Record<string, any>): ActionNode[] {
  return updateBranchParams(root, branchId, (params) => ({
    ...params,
    cases: (params.cases || []).map((c: any) => (c._id === caseId ? { ...c, ...patch } : c)),
  }));
}

/** Merges a patch into a branch node's action_parameters directly (e.g. its
 * dispatch `field`) — the general-purpose sibling of the case-specific
 * addCase/removeCase/updateCase helpers above. */
export function updateBranchField(root: ActionNode[], branchId: string, patch: Record<string, any>): ActionNode[] {
  return updateBranchParams(root, branchId, (params) => ({ ...params, ...patch }));
}

function updateBranchParams(root: ActionNode[], branchId: string, mutate: (params: any) => any): ActionNode[] {
  return root.map((n) => {
    if (n._id === branchId && n.action_type === 'branch') {
      return { ...n, action_parameters: mutate(n.action_parameters || {}) };
    }
    if (n.action_type === 'branch' && n.action_parameters) {
      const params = { ...n.action_parameters };
      let changed = false;
      if (Array.isArray(params.cases)) {
        params.cases = params.cases.map((c: any) => {
          const nextActions = updateBranchParams(c.actions || [], branchId, mutate);
          if (nextActions !== c.actions) changed = true;
          return { ...c, actions: nextActions };
        });
      }
      if (Array.isArray(params.default_actions)) {
        const nextDefault = updateBranchParams(params.default_actions, branchId, mutate);
        if (nextDefault !== params.default_actions) changed = true;
        params.default_actions = nextDefault;
      }
      return changed ? { ...n, action_parameters: params } : n;
    }
    return n;
  });
}

/** Turns a plain action node into a branch node, moving its own
 * configuration nowhere (it's discarded — branch and action config are
 * mutually exclusive on one node) and seeding one empty case. */
export function convertToBranch(root: ActionNode[], id: string): ActionNode[] {
  return walkLists(root, ({ list }) => {
    if (!list.some((n) => n._id === id)) return undefined;
    return list.map((n) =>
      n._id === id
        ? {
            _id: n._id,
            action_type: 'branch',
            action_parameters: {
              field: 'priority',
              cases: [{ operator: 'equal_to', expected_value: '', actions: [], _id: newId() }],
              default_actions: [],
            },
          }
        : n
    );
  });
}

export function makeActionNode(action_type = 'assign_agent'): ActionNode {
  return { _id: newId(), action_type, action_parameters: {} };
}
